import { AxiosError } from 'axios';
import {
  OrderDetailsResponseDto,
  OrderResponseDto,
  OrderCancelRequestDto,
  OrderMultiActionRequestDto,
  OrderChangeStatusRequestDto,
  OrderFulfillRequestDto,
  OrdersListResponseDto,
  EditDeliveryAddressRequestDto,
  OrderTotalResponseDto,
  OrdersExportResponseDto,
  HasFundsAvailableNuvemPagoResponseDto,
  PendingOrdersResponseDto,
  LogEntryResponseDto,
  OrderFulfillmentUpdateRequestDto,
  OrderProductsResponseDto,
  OrdersFiltersByTypeResponseDto,
  EditOrderRequestDto,
  OrderShippingCostRequestDto,
  OrderArchiveRequestDto,
  OrderUpdateResponseDto,
  RefundPaymentRequestDto,
  RefundAttemptStatusResponseDto,
  RefundPaymentResponseDto,
  OrderMultipleCountsResponseDto,
  OrderCountRequestDto,
  WholesaleDetailsResponseDto,
  ResolvedInvoiceStatusDto,
  OrderUnpackRequestDto,
  OrdersAutoCancelConfiguration,
  SavedSearchResponseDto,
  OrdersAutoCancelConfigurationByMethod,
  OrdersAutoCancelPaymentStatusConfiguration,
  SavedSearchRequestDto,
  ConfigureSavedSearchRequestDto,
  OrderChargebackResponseDto,
} from '@tiendanube/common';
import { OrdersListAggregationsResponseDto } from '@tiendanube/common/src/domains/orders/orders/OrderResponse';
import {
  Action,
  OrdersAutoCancelPaymentStatus,
  Status,
} from '@tiendanube/common/src/enums';
import axios from 'App/axios';
import { parseDateFrom, parseDateTo } from 'commons/utils/date';
import getUrlWithParams from 'commons/utils/getUrlWithParams';
import { getAuthStorage } from 'domains/Auth/authService/authService';
import { defaultStockIssuesFilters } from 'domains/Orders/constants';
import {
  FiltersType,
  FilterStatusEnum,
  UpdateBulkOrdersType,
  ExportFiltersType,
  FiltersParamsType,
  UpdateInvoiceEventType,
} from './constants';
import { fetchOrderDigest } from './digest';
import {
  CONFIGURE_SAVED_SEARCHES,
  CREATE_SAVED_SEARCH,
  EXPORT_ORDERS,
  FETCH_MULTIPLE_COUNTS_ORDERS,
  FETCH_ORDERS_AUTO_CANCEL_CONFIG,
  FETCH_ORDERS_FILTERS,
  FETCH_SAVED_SEARCHES,
  FETCH_TOTAL_ORDERS,
  ORDERS_BOUNCED_EMAILS_URL,
  PENDING_ORDERS_URL,
  UPDATE_SAVED_SEARCH,
} from './urls';
import { SavedSearchInterface } from '../ordersSlice/savedSearches';
import { ORDERS_LIST_TIMEOUT_ERROR } from '../pages/constants';

type FetchOrdersType = (
  filtersParams: FiltersType,
) => Promise<OrdersListResponseDto>;

// TODO: Remove when METAFILEDS ORDERS be full rollouted
const fetchOrders: FetchOrdersType = async (filtersParams) => {
  const paramsToRequest: FiltersType = { ...filtersParams };

  if (paramsToRequest.status === FilterStatusEnum.EMPTY) {
    paramsToRequest.status = Status.OPEN;
  }

  if (paramsToRequest.status === FilterStatusEnum.ALL) {
    paramsToRequest.status = FilterStatusEnum.EMPTY;
  }

  const { data } = await axios.get<OrdersListResponseDto>(
    `/v2/orders/list`,
    paramsToRequest && {
      params: paramsToRequest,
    },
  );
  return data;
};

const fetchOrdersAdvanced: FetchOrdersType = async (filtersParams) => {
  const payload = fetchOrderDigest(filtersParams);
  const timezone = (await getAuthStorage())?.timezone || 'UTC';

  if (!payload.status) {
    payload.status = Status.OPEN;
  }

  if (payload.status === FilterStatusEnum.ALL) {
    delete payload.status;
  }

  if (payload.dateFrom) {
    payload.dateFrom = parseDateFrom(payload.dateFrom, timezone);
  }

  if (payload.dateTo) {
    payload.dateTo = parseDateTo(payload.dateTo, timezone);
  }

  if (payload.exactProductSet === '') {
    delete payload.exactProductSet;
  }

  if (payload.couponIdsRaw === '') {
    delete payload.couponIdsRaw;
  }

  try {
    const { data } = await axios.post<OrdersListAggregationsResponseDto>(
      '/v3/orders/advanced-search',
      payload,
    );
    return data;
  } catch (error) {
    if (
      (error as AxiosError).response?.data.response.errorType ===
      ORDERS_LIST_TIMEOUT_ERROR
    ) {
      throw new Error(ORDERS_LIST_TIMEOUT_ERROR);
    } else {
      throw error;
    }
  }
};

const fetchBulkOrders = async (filters: FiltersParamsType) => {
  const paramsToRequest = Object.keys(filters).reduce((acc, key) => {
    if (
      filters[key] !== undefined &&
      filters[key] !== null &&
      filters[key] !== ''
    ) {
      acc[key] = filters[key];
    }
    return acc;
  }, {} as FiltersParamsType);
  const { data } = await axios.get('v2/orders/fetchBulkIds', {
    params: paramsToRequest,
  });
  const { results } = data;
  return results;
};

const fetchTotalOrders = async (
  filtersParams: ExportFiltersType,
): Promise<number> => {
  const paramsToRequest: ExportFiltersType = {
    ...filtersParams,
    ...(filtersParams.products && {
      variantIds: filtersParams.products.toString().split(';'),
      couponIds: filtersParams.couponIdsRaw?.toString().split(','),
    }),
    ...(filtersParams.couponIdsRaw && {
      couponIds: filtersParams.couponIdsRaw?.toString().split(','),
    }),
  };
  const timezone = (await getAuthStorage())?.timezone || 'UTC';

  if (!paramsToRequest.status) {
    paramsToRequest.status = Status.OPEN;
  }
  if (paramsToRequest.status === FilterStatusEnum.ALL) {
    paramsToRequest.status = FilterStatusEnum.EMPTY;
  }

  if (paramsToRequest.dateFrom) {
    paramsToRequest.dateFrom = parseDateFrom(
      paramsToRequest.dateFrom,
      timezone,
    );
  }

  if (paramsToRequest.dateTo) {
    paramsToRequest.dateTo = parseDateTo(paramsToRequest.dateTo, timezone);
  }

  if (paramsToRequest.exactProductSet === '') {
    delete paramsToRequest.exactProductSet;
  }

  if (paramsToRequest.couponIdsRaw === '') {
    delete paramsToRequest.couponIdsRaw;
  }

  if (paramsToRequest.stockIssues === '') {
    delete paramsToRequest.stockIssues;
  }

  const { data } = await axios.get<OrderTotalResponseDto>(
    FETCH_TOTAL_ORDERS,
    paramsToRequest && {
      params: paramsToRequest,
    },
  );
  return data.total;
};

const fetchSavedSearchesTotalOrders = async (
  savedSearches: SavedSearchInterface[],
): Promise<OrderMultipleCountsResponseDto> => {
  const paramsRequest: OrderCountRequestDto[] = [];
  // For now we only support default saved searches
  // Note that we use the key since id depends on DB value after initial save
  savedSearches
    .filter((ss) => ss.isDefault)
    .forEach((savedSearch) => {
      paramsRequest.push({
        key: savedSearch.key,
        filter: savedSearch.filters,
      });
    });

  const { data } = await axios.post<OrderMultipleCountsResponseDto>(
    FETCH_MULTIPLE_COUNTS_ORDERS,
    paramsRequest,
  );
  return data;
};

const fetchStockIssuesCountOrders = async (
  since: string,
): Promise<OrderMultipleCountsResponseDto> => {
  const { data } = await axios.post<OrderMultipleCountsResponseDto>(
    FETCH_MULTIPLE_COUNTS_ORDERS,
    [
      {
        key: 'withStockIssues',
        filter: {
          ...defaultStockIssuesFilters,
          dateFrom: since,
          dateTo: new Date().toISOString(),
        },
      },
    ],
  );
  return data;
};

const fetchSavedSearches = async (): Promise<SavedSearchResponseDto[]> => {
  const { data } = await axios.get<SavedSearchResponseDto[]>(
    FETCH_SAVED_SEARCHES,
  );
  return data;
};

const createSavedSearch = async (
  payload: SavedSearchRequestDto,
): Promise<SavedSearchResponseDto[]> => {
  const { data } = await axios.post<SavedSearchResponseDto[]>(
    CREATE_SAVED_SEARCH,
    payload,
  );
  return data;
};

const updateSavedSearch = async (
  id: string,
  payload: SavedSearchRequestDto,
): Promise<SavedSearchResponseDto[]> => {
  const { data } = await axios.patch<SavedSearchResponseDto[]>(
    `${UPDATE_SAVED_SEARCH}/${id}`,
    payload,
  );
  return data;
};

const configureSavedSearches = async (
  payload: ConfigureSavedSearchRequestDto[],
): Promise<SavedSearchResponseDto[]> => {
  const { data } = await axios.put<SavedSearchResponseDto[]>(
    CONFIGURE_SAVED_SEARCHES,
    payload,
  );
  return data;
};

const exportOrders = async (
  filtersParams: ExportFiltersType,
): Promise<OrdersExportResponseDto> => {
  // Convert pipe separator to comma for payment provider before sending to backend
  // (paymentProvider uses pipe to avoid conflicts with commas in provider IDs, ex: "mercadopago,1234")
  const paymentProvider = filtersParams.paymentProvider
    ? filtersParams.paymentProvider.replace(/\|/g, ',')
    : filtersParams.paymentProvider;

  const paramsToRequest = {
    ...filtersParams,
    paymentProvider,
    ...(filtersParams.products && {
      variantIds: filtersParams.products.split(';'),
    }),
  };

  if (!paramsToRequest.status) {
    paramsToRequest.status = Status.OPEN;
  }
  if (paramsToRequest.status === FilterStatusEnum.ALL) {
    paramsToRequest.status = FilterStatusEnum.EMPTY;
  }

  if (paramsToRequest.exactProductSet === '') {
    delete paramsToRequest.exactProductSet;
  }

  if (paramsToRequest.couponIdsRaw === '') {
    delete paramsToRequest.couponIdsRaw;
  }

  if (paramsToRequest.stockIssues === '') {
    delete paramsToRequest.stockIssues;
  }

  const { data } = await axios.post<OrdersExportResponseDto>(
    EXPORT_ORDERS,
    paramsToRequest,
  );
  return data;
};

const fetchOrderById = async (id: string): Promise<OrderDetailsResponseDto> => {
  const { data } = await axios.get<OrderDetailsResponseDto>(`/v2/orders/${id}`);
  return data;
};

const fetchOrderProducts = async (
  id: string,
): Promise<OrderProductsResponseDto> => {
  const { data } = await axios.get<OrderProductsResponseDto>(
    `/v2/orders/${id}/products`,
  );
  return data;
};

const updateBulkOrders: UpdateBulkOrdersType = async ({
  actionType,
  orderIds,
  action,
}) => {
  const payload: OrderMultiActionRequestDto = {
    actionType: actionType,
    collectionsIds: orderIds.map((id) => id.toString()),
    action: actionType === Action.PAY ? { status: Status.PAID } : action,
  };
  const response = await axios.patch<OrderResponseDto[]>(
    '/v2/orders/bulk',
    payload,
  );
  return response.data;
};

const fetchCancelOrder = async (id: string): Promise<any> => {
  const response = await axios.patch(`/v2/orders/${id}/cancel`);
  return response.data;
};

const fetchHasAvailableFunds = async (
  amount: number,
  app: 'nuvempago' | 'pagonube',
): Promise<HasFundsAvailableNuvemPagoResponseDto> => {
  const response = await axios.get(`/v2/orders/can-refund`, {
    params: {
      amount: `${amount}`,
      app,
    },
  });
  return response.data;
};

const fetchArchiveOrder = async (
  id: string,
  payload: OrderArchiveRequestDto,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}/archive`,
    payload,
  );
  return response.data;
};

const fetchOrderPatch = async (
  id: string,
  action: OrderChangeStatusRequestDto,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}`,
    action,
  );
  return response.data;
};

const cancelById = async (
  id: string,
  action: OrderCancelRequestDto,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}/cancel`,
    action,
  );
  return response.data;
};

const refundById = async (
  id: string,
  action: RefundPaymentRequestDto,
): Promise<RefundPaymentResponseDto> => {
  try {
    const response = await axios.post<RefundPaymentResponseDto>(
      `/v2/orders/${id}/refund`,
      action,
    );
    return response.data;
  } catch (error) {
    const errorCode = error.response?.data?.response?.error_code;
    if (error?.isAxiosError) {
      throw new Error(errorCode);
    }
    throw error;
  }
};

const fetchRefundAttemptStatus = async (
  orderId: string,
  refundAttemptId: string,
): Promise<RefundAttemptStatusResponseDto> => {
  const response = await axios.get<RefundAttemptStatusResponseDto>(
    `/v2/orders/${orderId}/refund/${refundAttemptId}`,
  );
  return response.data;
};

const openById = async (
  id: string,
  validateStock?: boolean,
  isActionFromOrdersList?: boolean,
): Promise<OrderUpdateResponseDto> => {
  const response = await axios.patch<OrderUpdateResponseDto>(
    `/v2/orders/${id}`,
    {
      status: Status.OPEN,
      validateStock,
      isActionFromOrdersList,
    },
  );
  return response.data;
};

const paidById = async (
  id: string,
  gateway?: string,
  gatewayMethod?: string,
  gatewayName?: string,
  isActionFromOrdersList?: boolean,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}`,
    {
      status: Status.PAID,
      isActionFromOrdersList,
      ...(gateway || gatewayMethod
        ? {
            gatewayInformation: {
              gateway,
              gatewayMethod,
              gatewayName,
            },
          }
        : {}),
    },
  );
  return response.data;
};

const partiallyPaidById = async (
  id: string,
  method: string,
  name?: string,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}/partial-pay`,
    { method, name },
  );
  return response.data;
};

const remarkById = async (
  id: string,
  text: string,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}/remarks`,
    {
      remarks: text,
    },
  );
  return response.data;
};

const packById = async (
  id: string,
  fulfillments: OrderFulfillmentUpdateRequestDto[],
  isActionFromOrdersList?: boolean,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}/pack`,
    { fulfillments, isActionFromOrdersList },
  );
  return response.data;
};

const fetchTimeline = async (id: string): Promise<LogEntryResponseDto[]> => {
  const { data } = await axios.get<LogEntryResponseDto[]>(
    `/v2/orders/timeline/${id}`,
  );
  return data;
};

const fulfillById = async (
  id: string,
  action: OrderFulfillRequestDto,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${id}/fulfill`,
    action,
  );
  return response.data;
};

type EditDeliveryAddressParams = {
  id: string;
  deliveryAddress: EditDeliveryAddressRequestDto;
};

const editDeliveryAddress = async (
  params: EditDeliveryAddressParams,
): Promise<void> => {
  await axios.put(
    `/v2/orders/${params.id}/delivery-address`,
    params.deliveryAddress,
  );
};

const unpackById = async (
  orderId: string,
  payload: OrderUnpackRequestDto,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `v2/orders/${orderId}/unpack`,
    {
      fulfillments: payload.fulfillments ?? [],
      isActionFromOrdersList: payload.isActionFromOrdersList,
    },
  );
  return response.data;
};

const getPendingOrders = async () => {
  const { data } = await axios.get<PendingOrdersResponseDto>(
    PENDING_ORDERS_URL,
  );

  return data;
};

const getOrdersFilters = async () => {
  const { data } = await axios.get<OrdersFiltersByTypeResponseDto>(
    FETCH_ORDERS_FILTERS,
  );

  return data;
};

const getOrdersAutoCancelConfiguration = async () => {
  const { data } = await axios.get<OrdersAutoCancelConfiguration>(
    FETCH_ORDERS_AUTO_CANCEL_CONFIG,
  );

  return data;
};

const saveOrdersAutoCancelConfiguration = async (
  configuration: OrdersAutoCancelConfigurationByMethod,
) => {
  const paymentStatusConfig = Object.keys(OrdersAutoCancelPaymentStatus).reduce(
    (acc, status) => {
      acc[status.toLowerCase()] = configuration.configuration;
      return acc;
    },
    {},
  );
  const configToSave: OrdersAutoCancelConfiguration = {
    configuration: paymentStatusConfig as Record<
      string,
      OrdersAutoCancelPaymentStatusConfiguration
    >,
    updateStock: configuration.updateStock,
    notifyClient: configuration.notifyClient,
    isNew: configuration.isNew,
  };
  await axios.post(`/v2/orders/auto-cancel`, configToSave);
};

const editOrder = async (id: string, editRequest: EditOrderRequestDto) => {
  await axios.patch(`/v2/orders/${id}/edit`, editRequest);
};

const getOrderBouncedEmails = async (id: string) => {
  const { data } = await axios.get(
    getUrlWithParams(ORDERS_BOUNCED_EMAILS_URL, { id }),
  );
  return data;
};

const getOrderShippingCost = async (
  id: string,
  payload: OrderShippingCostRequestDto,
  signal?: AbortSignal,
) => {
  const { data } = await axios.post(`/v2/orders/${id}/shipping-cost`, payload, {
    signal,
  });
  return data;
};

const invoiceRequestById = async (
  orderId: string,
  fulfillmentOrderId: string,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.post<OrderDetailsResponseDto>(
    `/v2/orders/${orderId}/invoice`,
    {
      fulfillmentOrderId,
    },
  );
  return response.data;
};

const updateInvoiceById = async (
  orderId: string,
  fulfillmentOrderId: string,
  reason: string,
  eventType: UpdateInvoiceEventType,
): Promise<OrderDetailsResponseDto> => {
  const response = await axios.patch<OrderDetailsResponseDto>(
    `/v2/orders/${orderId}/invoice`,
    {
      fulfillmentOrderId,
      reason,
      eventType,
    },
  );
  return response.data;
};

const resolvedInvoices = async (
  orderId: string,
  fulfillmentOrderId: string,
): Promise<ResolvedInvoiceStatusDto> => {
  const response = await axios.get<ResolvedInvoiceStatusDto>(
    `v2/orders/${orderId}/fulfillment-orders/${fulfillmentOrderId}/resolvedInvoices`,
  );
  return response.data;
};

const getOrderWholesaleDetails = async (
  orderId: string,
): Promise<WholesaleDetailsResponseDto> => {
  const response = await axios.get<WholesaleDetailsResponseDto>(
    `/v2/orders/${orderId}/wholesale-details`,
  );
  return response.data;
};

const getOrdersChargebacks = async (): Promise<OrderChargebackResponseDto> => {
  const response = await axios.get<OrderChargebackResponseDto>(
    `/v2/orders/chargebacks`,
  );
  return response.data;
};

const ordersService = {
  fulfillById,
  fetchTimeline,
  packById,
  unpackById,
  paidById,
  partiallyPaidById,
  remarkById,
  openById,
  cancelById,
  refundById,
  fetchOrderPatch,
  fetchArchiveOrder,
  fetchCancelOrder,
  fetchHasAvailableFunds,
  fetchOrders,
  fetchOrdersAdvanced,
  fetchOrderById,
  fetchTotalOrders,
  exportOrders,
  updateBulkOrders,
  editDeliveryAddress,
  fetchBulkOrders,
  getPendingOrders,
  fetchOrderProducts,
  getOrdersFilters,
  editOrder,
  getOrderBouncedEmails,
  getOrderShippingCost,
  fetchRefundAttemptStatus,
  invoiceRequestById,
  fetchSavedSearchesTotalOrders,
  getOrderWholesaleDetails,
  resolvedInvoices,
  updateInvoiceById,
  getOrdersAutoCancelConfiguration,
  saveOrdersAutoCancelConfiguration,
  fetchStockIssuesCountOrders,
  getOrdersChargebacks,
  fetchSavedSearches,
  createSavedSearch,
  updateSavedSearch,
  configureSavedSearches,
};

export default ordersService;
